import { type hfb } from "@repo/api";
import { type Values } from "@repo/utils";

export type ConsentStatusType =
  "MISSING" | "PENDING" | "APPROVED" | "NOT_APPROVED" | "ERROR";

export const PreferredContactMethod = {
  phone: "PHONE",
  email: "EMAIL",
} as const;

export const CustomerReadiness = {
  exploring: "Exploring",
  needsAssistance: "Needs Assistance",
  readyToBuy: "Ready to Buy",
} as const;

export const CustomerInterestIndex = {
  low: "Low",
  medium: "Medium",
  high: "High",
} as const;

export interface Customer {
  // name
  firstName: string;
  lastName: string;
  // contact details
  email: string;
  postalCode: string;
  phoneNumber: string;
  city: string;
  streetAddress1: string;
  streetAddress2?: string;
  streetAddress3?: string;
  state?: string;
  preferredContactType?: string;
  language?: string; // <= Old CP
  languageCode?: string; // TODO: temporary support. Will be removed
  country?: string;
  countryCode?: string; // TODO: temporary support. Will be removed
  customerLocale?: string;
  // other details
  customerType: "Business" | "Individual";
  familyNumber?: string;
  partyId: string;
  customerAccountState?: "UNREGISTERED" | "REGISTERED";
  // Business customer
  organizationNumber?: string;
  organizationName?: string;
  bcmId?: string;
  consents?: {
    privacyPolicy?: boolean;
    termsAndCondition?: boolean;
  };
  // local implementation
  timezone?: string[]; // timezones associated with customer's state. It can be multiple timezones
}

export type ConsentType = "FOLLOWUP_CONSENT" | "PROMOTIONAL_CONSENT";

export interface CustomerConsent {
  consentId?: number;
  consentType: ConsentType;
  consentProvided?: boolean;
  customerCommunicationSendStatus?: "ERROR" | "SUCCESS";
  hfb?: hfb[];
  updatedAt?: string;
  opportunityId?: number; // if consent is connected to a project
}

export interface ConsentMessage {
  type: "EMAIL" | "PHONE";
  contact: string;
}

interface CustomerPayload {
  firstName: string;
  lastName: string;
  preferredContactType: Values<typeof PreferredContactMethod>;
  email: string;
  phone: string;
  partyId: string;
  customerType: Customer["customerType"];
  familyCardNumber?: string;
  bcmId?: string;
  organizationNumber?: string;
  organizationName?: string;
  customerLocale?: string;
}

export interface CustomerConsentPayload extends CustomerPayload {
  customerConsents: CustomerConsent[];
  captureConsentMessage?: ConsentMessage;
}

export const getCustomerPayload = (customer: Customer) => {
  return {
    firstName: customer.firstName,
    lastName: customer.lastName,
    email: customer.email,
    phone: customer.phoneNumber,
    partyId: customer.partyId,
    customerType: customer.customerType,
    familyCardNumber: customer.familyNumber,
    bcmId: customer.bcmId,
    organizationNumber: customer.organizationName,
    customerLocale:
      customer.language?.toLowerCase() ??
      customer.customerLocale?.toLowerCase() ??
      "en",
    preferredContactType: customer.preferredContactType ?? "EMAIL", // TODO: FIX hardcode
  };
};

export interface CustomerData {
  customerSelection: CustomerSelection;
}

export interface CustomerSearchError {
  success?: boolean;
  errorType?: string;
  error?: {
    message: string;
    name: string;
    code: string;
    status: number;
  };
}
